             

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Tooltip,
  Divider,
  TablePagination,
  useTheme,
  useMediaQuery,
  Avatar,
  ListItemAvatar,
  Stack,
  Collapse,
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

// Icons
import QrCodeIcon from '@mui/icons-material/QrCode';
import EmailIcon from '@mui/icons-material/Email';
import ChatIcon from '@mui/icons-material/Chat';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import PersonIcon from '@mui/icons-material/Person';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import InfoIcon from '@mui/icons-material/Info';
import LinkIcon from '@mui/icons-material/Link';
import CheckIcon from '@mui/icons-material/Check';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Services
import {
  getVolunteersWithQRSummary,
  generateQRCodes,
  sendQRCodeEmails,
  sendQRCodeWhatsApp,
  deleteQRCode,
  downloadQRCodeImage,
  getQRCodeImage,
} from '../services/api';

// Types
import { VolunteerQRSummary, VolunteerQRCode, QRCodeGenerationRequest, QRCodeEmailRequest } from '../models/types';

const QRCodeManagementPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [volunteers, setVolunteers] = useState<VolunteerQRSummary[]>([]);
  const [filteredVolunteers, setFilteredVolunteers] = useState<VolunteerQRSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVolunteers, setSelectedVolunteers] = useState<string[]>([]);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Dialog states
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [qrCodePreviewOpen, setQrCodePreviewOpen] = useState(false);
  const [selectedVolunteerDetails, setSelectedVolunteerDetails] = useState<VolunteerQRSummary | null>(null);
  const [selectedQRCodeForPreview, setSelectedQRCodeForPreview] = useState<VolunteerQRCode | null>(null);
  const [qrCodeImageUrl, setQrCodeImageUrl] = useState<string | null>(null);
  
  // Form states
  const [includePartnerEntry, setIncludePartnerEntry] = useState(true);
  const [expiresInDays, setExpiresInDays] = useState<number>(30);
  const [qrCodeComment, setQrCodeComment] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  
  // Loading states
  const [generating, setGenerating] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [deletingQRCode, setDeletingQRCode] = useState<string | null>(null);

  // Filter states
  const [showOnlyNeedsNotification, setShowOnlyNeedsNotification] = useState(false);
  const [showOnlyWithUnusedCodes, setShowOnlyWithUnusedCodes] = useState(false);
  const [showOnlyDelivered, setShowOnlyDelivered] = useState(false);
  const [showOnlyNoSent, setShowOnlyNoSent] = useState(false);
  const [showOnlyPhoneNoEmail, setShowOnlyPhoneNoEmail] = useState(false);
  const [showOnlyWithEmail, setShowOnlyWithEmail] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  useEffect(() => {
    fetchVolunteers();
  }, []);

    // Word export for selected volunteers with QR codes
  const handleDownloadWordQRCodes = async () => {
    if (selectedVolunteers.length === 0) return;
    // Dynamically import docx to avoid bundle bloat
  const docx = await import('docx');
  const { Document, Packer, Paragraph, Table, TableRow, TableCell, WidthType } = docx;

    // Prepare rows: 2 columns per row, each cell contains only the QR code image
    const rows = [];
    const selectedVolunteerObjs = filteredVolunteers.filter(v => selectedVolunteers.includes(v.volunteerId));
    for (let i = 0; i < selectedVolunteerObjs.length; i += 2) {
      const cells = [];
      for (let j = 0; j < 2; j++) {
        const v = selectedVolunteerObjs[i + j];
        if (!v) {
          cells.push(new TableCell({ children: [new Paragraph("")], width: { size: 50, type: WidthType.PERCENTAGE } }));
          continue;
        }
        // Get most recent QR code
        const qr = v.qrCodes && v.qrCodes.length > 0 ? v.qrCodes[v.qrCodes.length - 1] : null;
        let qrImageParagraph = new Paragraph("");
        if (qr) {
          try {
            // Fetch QR code image from API
            const blob = await getQRCodeImage(qr.id);
            const arrayBuffer = await blob.arrayBuffer();
            const uint8Array = new Uint8Array(arrayBuffer);

            // Detect original image size
            const imageUrl = URL.createObjectURL(blob);
            const img = document.createElement('img');
            img.src = imageUrl;
            // Await image load to get natural size
            await new Promise((resolve, reject) => {
              img.onload = () => resolve(true);
              img.onerror = reject;
            });
            const width = img.naturalWidth;
            const height = img.naturalHeight;
            URL.revokeObjectURL(imageUrl);

            qrImageParagraph = new Paragraph({
              children: [
                new docx.ImageRun({
                  data: uint8Array,
                  type: "png",
                  transformation: { width: width / 2, height: height / 2 }
                })
              ]
            });
          } catch (err) {
            qrImageParagraph = new Paragraph("QR-Code Bild konnte nicht geladen werden.");
          }
        }
        cells.push(new TableCell({
          children: [
            qrImageParagraph
          ],
          width: { size: 50, type: WidthType.PERCENTAGE }
        }));
      }
      rows.push(new TableRow({ children: cells }));
    }
    const table = new Table({ rows, width: { size: 100, type: WidthType.PERCENTAGE } });
    const doc = new Document({
      sections: [{
        children: [
          table
        ]
      }]
    });
    const blob = await Packer.toBlob(doc);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `qr-codes-druck.docx`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }, 1000);
  };

  const fetchVolunteers = async () => {
    try {
      setLoading(true);
      const data = await getVolunteersWithQRSummary();
      setVolunteers(data);
    } catch (error) {
      console.error('Error fetching volunteers:', error);
      setError('Freiwillige konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = useCallback(() => {
    let filtered = volunteers;

    // Search filter
    if (searchTerm) {
      const lowercaseSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(volunteer =>
        volunteer.volunteerName.toLowerCase().includes(lowercaseSearch) ||
        volunteer.volunteerEmail.toLowerCase().includes(lowercaseSearch)
      );
    }

    // Needs notification filter
    if (showOnlyNeedsNotification) {
      filtered = filtered.filter(volunteer => volunteer.needsNotification);
    }

    // Has unused codes filter
    if (showOnlyWithUnusedCodes) {
      filtered = filtered.filter(volunteer => volunteer.hasUnusedQRCode);
    }

    // Has delivered codes filter
    if (showOnlyDelivered) {
      filtered = filtered.filter(volunteer => volunteer.hasQRCodeDelivered);
    }

    // No sent email or WhatsApp
    if (showOnlyNoSent) {
      filtered = filtered.filter(volunteer => {
        // Check all QR codes for not sent
        return volunteer.qrCodes.every(qr => !qr.isEmailSent && !qr.isWhatsAppSent);
      });
    }

    // Only with phone and without email
    if (showOnlyPhoneNoEmail) {
      filtered = filtered.filter(volunteer => !!volunteer.phoneNumber && !volunteer.volunteerEmail);
    }

    // Only with email
    if (showOnlyWithEmail) {
      filtered = filtered.filter(volunteer => !!volunteer.volunteerEmail);
    }

    setFilteredVolunteers(filtered);
    setPage(0); // Reset to first page when filtering
  }, [volunteers, searchTerm, showOnlyNeedsNotification, showOnlyWithUnusedCodes, showOnlyDelivered, showOnlyNoSent, showOnlyPhoneNoEmail, showOnlyWithEmail]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Helper function to get the active (most recent unused) QR code for a volunteer
  const getActiveQRCode = (volunteer: VolunteerQRSummary): VolunteerQRCode | null => {
    // Get unused QR codes sorted by creation date (most recent first)
    const unusedQRCodes = volunteer.qrCodes
      .filter(qr => !qr.isUsed)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    
    return unusedQRCodes.length > 0 ? unusedQRCodes[0] : null;
  };

  const handleSelectVolunteer = (volunteerId: string) => {
    setSelectedVolunteers(prev => 
      prev.includes(volunteerId)
        ? prev.filter(id => id !== volunteerId)
        : [...prev, volunteerId]
    );
  };

  const handleCloseGenerateDialog = () => {
    setGenerateDialogOpen(false);
    setQrCodeComment(''); // Reset comment when dialog is closed
  };

  const handleGenerateQRCodes = async () => {
    if (selectedVolunteers.length === 0) return;

    try {
      setGenerating(true);
      const request: QRCodeGenerationRequest = {
        volunteerIds: selectedVolunteers,
        includePartnerEntry,
        expiresInDays: expiresInDays > 0 ? expiresInDays : undefined,
        comment: qrCodeComment.trim() || undefined,
      };

      await generateQRCodes(request);
      await fetchVolunteers(); // Refresh data
      setSelectedVolunteers([]);
      setGenerateDialogOpen(false);
      
      // Reset form
      setQrCodeComment('');
      
      // Show success message
      setError(null);
    } catch (error) {
      console.error('Error generating QR codes:', error);
      setError('Fehler beim Generieren der QR-Codes.');
    } finally {
      setGenerating(false);
    }
  };

  const handleSendEmails = async () => {
    const qrCodesToSend = selectedVolunteers.flatMap(volunteerId => {
      const volunteer = volunteers.find(v => v.volunteerId === volunteerId);
      return volunteer?.qrCodes.filter(qr => !qr.isUsed && !qr.isEmailSent).map(qr => qr.id) || [];
    });

    if (qrCodesToSend.length === 0) {
      setError('Keine unbenutzten QR-Codes zum Versenden gefunden.');
      return;
    }

    try {
      setSendingEmails(true);
      const request: QRCodeEmailRequest = {
        qrCodeIds: qrCodesToSend,
        subject: emailSubject.trim(),
        message: emailMessage.trim(),
      };

      await sendQRCodeEmails(request);
      await fetchVolunteers(); // Refresh data
      setSelectedVolunteers([]);
      setEmailDialogOpen(false);
      setEmailSubject('');
      setEmailMessage('');
      
      // Show success message
      setError(null);
    } catch (error) {
      console.error('Error sending emails:', error);
      setError('Fehler beim Versenden der E-Mails.');
    } finally {
      setSendingEmails(false);
    }
  };

  const handleSendWhatsApp = async (volunteer: VolunteerQRSummary) => {
    const activeQRCode = getActiveQRCode(volunteer);
    if (!activeQRCode || !volunteer.phoneNumber) return;

    try {
      const result = await sendQRCodeWhatsApp(activeQRCode.id);
      window.open(result.whatsappUrl, '_blank');
      await fetchVolunteers(); // Refresh data
    } catch (error) {
      console.error('Error sending WhatsApp:', error);
      setError('Fehler beim Senden der WhatsApp-Nachricht.');
    }
  };

  const handleDeleteQRCode = async (qrCodeId: string) => {
    try {
      setDeletingQRCode(qrCodeId);
      await deleteQRCode(qrCodeId);
      await fetchVolunteers(); // Refresh data
    } catch (error) {
      console.error('Error deleting QR code:', error);
      setError('Fehler beim Löschen des QR-Codes.');
    } finally {
      setDeletingQRCode(null);
    }
  };

  const handleViewDetails = (volunteer: VolunteerQRSummary) => {
    setSelectedVolunteerDetails(volunteer);
    setDetailsDialogOpen(true);
  };

  const downloadQRCode = (qrCode: VolunteerQRCode) => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${qrCode.qrCodeData}`;
    link.download = `qr-code-${qrCode.volunteerName.replace(/\s+/g, '-')}-${qrCode.id.substring(0, 8)}.png`;
    link.click();
  };

  const handleDownloadQRCode = async (qrCode: VolunteerQRCode) => {
    try {
      const fileName = `qr-code-${qrCode.volunteerName.replace(/\s+/g, '-')}-${qrCode.id.substring(0, 8)}.png`;
      await downloadQRCodeImage(qrCode.id, fileName);
    } catch (error) {
      console.error('Error downloading QR code:', error);
      setError('Fehler beim Herunterladen des QR-Codes.');
    }
  };

  const handlePreviewQRCode = async (qrCode: VolunteerQRCode) => {
    try {
      const blob = await getQRCodeImage(qrCode.id);
      const url = URL.createObjectURL(blob);
      setQrCodeImageUrl(url);
      setSelectedQRCodeForPreview(qrCode);
      setQrCodePreviewOpen(true);
    } catch (error) {
      console.error('Error loading QR code image:', error);
      setError('Fehler beim Laden des QR-Code-Bildes.');
    }
  };

  const handleClosePreview = () => {
    if (qrCodeImageUrl) {
      URL.revokeObjectURL(qrCodeImageUrl);
      setQrCodeImageUrl(null);
    }
    setSelectedQRCodeForPreview(null);
    setQrCodePreviewOpen(false);
  };

  const handleCopyQRCodeLink = async (qrCode: VolunteerQRCode) => {
    try {
      const baseUrl = window.location.origin;
      const qrCodeImageUrl = `${baseUrl}/api/volunteer/qr-codes/image/${qrCode.id}`;
      await navigator.clipboard.writeText(qrCodeImageUrl);
      // You could add a toast notification here
      console.log('QR code link copied to clipboard');
    } catch (error) {
      console.error('Error copying link:', error);
      setError('Fehler beim Kopieren des Links.');
    }
  };

  const renderVolunteerCard = (volunteer: VolunteerQRSummary) => {
    const activeQRCode = getActiveQRCode(volunteer);
    
    return (
      <Card 
        key={volunteer.volunteerId} 
        sx={{ 
          mb: 2, 
          position: 'relative',
          border: selectedVolunteers.includes(volunteer.volunteerId) ? 2 : 1,
          borderColor: selectedVolunteers.includes(volunteer.volunteerId) ? 'primary.main' : 'divider',
          '&:hover': {
            boxShadow: 3,
          },
        }}
      >
        {/* Selection Indicator */}
        {selectedVolunteers.includes(volunteer.volunteerId) && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'primary.main',
              borderRadius: '50%',
              p: 0.5,
              zIndex: 1,
            }}
          >
            <CheckIcon sx={{ color: 'white', fontSize: 16 }} />
          </Box>
        )}

        <CardContent sx={{ pb: 1 }}>
          {/* Header with Avatar and Basic Info */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
            <ListItemAvatar>
              <Avatar sx={{ bgcolor: volunteer.earnedGoodie ? 'success.main' : 'grey.400' }}>
                <PersonIcon />
              </Avatar>
            </ListItemAvatar>
            <Box sx={{ flexGrow: 1, ml: 1 }}>
              <Typography variant="h6" component="div" sx={{ lineHeight: 1.2, mb: 0.5 }}>
                {volunteer.volunteerName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {volunteer.volunteerEmail}
              </Typography>
              {volunteer.phoneNumber && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {volunteer.phoneNumber}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Status Chips */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip
              icon={<AccessTimeIcon />}
              label={`${volunteer.totalWorkingHours}h`}
              color={volunteer.earnedGoodie ? 'success' : 'default'}
              size="small"
              variant="outlined"
            />
            
            {volunteer.qrCodes.length > 0 ? (
              <Chip
                icon={<QrCodeIcon />}
                label={`${volunteer.qrCodes.length} QR-Code${volunteer.qrCodes.length !== 1 ? 's' : ''}`}
                color="primary"
                size="small"
                variant="outlined"
              />
            ) : (
              <Chip
                icon={<QrCodeIcon />}
                label="Kein QR-Code"
                color="default"
                size="small"
                variant="outlined"
              />
            )}

            {volunteer.hasUnusedQRCode && (
              <Chip
                icon={<CheckCircleIcon />}
                label="Bereit"
                color="success"
                size="small"
              />
            )}

            {volunteer.needsNotification && (
              <Chip
                icon={<WarningIcon />}
                label="Aktion erforderlich"
                color="warning"
                size="small"
              />
            )}

            {volunteer.hasQRCodeDelivered && (
              <Chip
                icon={<SendIcon />}
                label="Versendet"
                color="info"
                size="small"
              />
            )}
          </Box>

          {/* Quick Info */}
          {activeQRCode && (
            <Box sx={{ 
              bgcolor: 'grey.50', 
              p: 1.5, 
              borderRadius: 1, 
              border: '1px solid',
              borderColor: 'grey.200'
            }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Aktueller QR-Code:
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2">
                  {format(parseISO(activeQRCode.createdAt), 'dd.MM.yyyy HH:mm', { locale: de })}
                  {activeQRCode.includesPartnerEntry && ' (+ Partner)'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {activeQRCode.isEmailSent && (
                    <Tooltip title="Per E-Mail versendet">
                      <EmailIcon sx={{ fontSize: 16, color: 'info.main' }} />
                    </Tooltip>
                  )}
                  {activeQRCode.isWhatsAppSent && (
                    <Tooltip title="Per WhatsApp versendet">
                      <ChatIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </CardContent>

        <CardActions sx={{ pt: 0, flexWrap: 'wrap', gap: 0.5 }}>
          {/* Selection Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedVolunteers.includes(volunteer.volunteerId)}
                onChange={() => handleSelectVolunteer(volunteer.volunteerId)}
                color="primary"
              />
            }
            label="Für Gruppenaktionen auswählen"
            sx={{ mr: 2 }}
          />

          {/* Quick Actions */}
          <Button
            size="small"
            onClick={() => {
              setSelectedVolunteers([volunteer.volunteerId]);
              setGenerateDialogOpen(true);
            }}
            disabled={volunteer.totalWorkingHours === 0}
            color="primary"
            variant="contained"
            startIcon={<QrCodeIcon />}
          >
            QR-Code erstellen
          </Button>

          {activeQRCode && (
            <>
              <Tooltip title="QR-Code anzeigen">
                <Button
                  size="small"
                  onClick={() => handlePreviewQRCode(activeQRCode)}
                  color="primary"
                  variant="outlined"
                  sx={{ minWidth: 'auto', px: 1 }}
                >
                  <InfoIcon />
                </Button>
              </Tooltip>

              <Tooltip title="Herunterladen">
                <Button
                  size="small"
                  onClick={() => handleDownloadQRCode(activeQRCode)}
                  color="primary"
                  variant="outlined"
                  sx={{ minWidth: 'auto', px: 1 }}
                >
                  <DownloadIcon />
                </Button>
              </Tooltip>

              {volunteer.phoneNumber && (
                <Tooltip title="WhatsApp senden">
                  <Button
                    size="small"
                    onClick={() => handleSendWhatsApp(volunteer)}
                    color="success"
                    variant="outlined"
                    sx={{ minWidth: 'auto', px: 1 }}
                  >
                    <ChatIcon />
                  </Button>
                </Tooltip>
              )}
            </>
          )}

          {/* More Details */}
          <Button
            size="small"
            onClick={() => handleViewDetails(volunteer)}
            sx={{ marginLeft: 'auto' }}
          >
            Alle Details
          </Button>
        </CardActions>
      </Card>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ position: 'relative' }}>
      <Box sx={{ mt: 2, mb: 4 }}>
        {/* Header Section */}
        <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', color: 'white' }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            QR-Code Management
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, mb: 2 }}>
            Einfache Verwaltung von QR-Codes für kostenlosen Eintritt
          </Typography>
          
          {/* Quick Stats */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 2, mt: 1 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {volunteers.length}
              </Typography>
              <Typography variant="caption">
                Freiwillige
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {volunteers.filter(v => v.needsNotification).length}
              </Typography>
              <Typography variant="caption">
                Benötigen QR-Code
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {volunteers.filter(v => v.hasUnusedQRCode).length}
              </Typography>
              <Typography variant="caption">
                Bereit zum Versenden
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {volunteers.filter(v => v.hasQRCodeDelivered).length}
              </Typography>
              <Typography variant="caption">
                Bereits versendet
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Search and Filter Section */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack spacing={2}>
            {/* Main Search Bar */}
            <TextField
              fullWidth
              placeholder="Nach Name oder E-Mail suchen..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm('')}
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />

            {/* Filter Toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button
                startIcon={<FilterListIcon />}
                endIcon={filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => setFiltersExpanded(!filtersExpanded)}
                variant="outlined"
                size="small"
              >
                Filter ({[showOnlyNeedsNotification, showOnlyWithUnusedCodes, showOnlyDelivered].filter(Boolean).length})
              </Button>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={fetchVolunteers}
                  disabled={loading}
                  size="small"
                >
                  Aktualisieren
                </Button>
              </Box>
            </Box>

            {/* Bulk Selection Actions */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                Schnellauswahl:
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  const needsNotification = volunteers.filter(v => v.needsNotification);
                  setSelectedVolunteers(needsNotification.map(v => v.volunteerId));
                }}
                disabled={volunteers.filter(v => v.needsNotification).length === 0}
              >
                Alle benötigen QR-Code ({volunteers.filter(v => v.needsNotification).length})
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  const currentPageVolunteers = filteredVolunteers.slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage
                  );
                  setSelectedVolunteers(currentPageVolunteers.map(v => v.volunteerId));
                }}
                disabled={filteredVolunteers.length === 0}
              >
                Alle auf Seite
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => setSelectedVolunteers(filteredVolunteers.map(v => v.volunteerId))}
              >
              Alle aktuell gefilterten Helfer auswählen
              </Button>
              
              {selectedVolunteers.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={() => setSelectedVolunteers([])}
                >
                  Auswahl löschen
                </Button>

              )}
            </Box>

            {/* Expandable Filters */}
            <Collapse in={filtersExpanded}>
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 1, 
                p: 2, 
                bgcolor: 'grey.50', 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200'
              }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showOnlyNeedsNotification}
                      onChange={(e) => setShowOnlyNeedsNotification(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Benötigen QR-Code"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showOnlyWithUnusedCodes}
                      onChange={(e) => setShowOnlyWithUnusedCodes(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Mit unbenutzten Codes"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showOnlyDelivered}
                      onChange={(e) => setShowOnlyDelivered(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Bereits versendet"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showOnlyNoSent}
                      onChange={(e) => setShowOnlyNoSent(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Ohne gesendete E-Mail/WhatsApp"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showOnlyPhoneNoEmail}
                      onChange={(e) => setShowOnlyPhoneNoEmail(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Nur mit Telefonnummer, ohne E-Mail"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showOnlyWithEmail}
                      onChange={(e) => setShowOnlyWithEmail(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Nur mit E-Mail"
                />
                {/* Clear Filters */}
                {[
                  showOnlyNeedsNotification,
                  showOnlyWithUnusedCodes,
                  showOnlyDelivered,
                  showOnlyNoSent,
                  showOnlyPhoneNoEmail,
                  showOnlyWithEmail
                ].some(Boolean) && (
                  <Button
                    size="small"
                    startIcon={<ClearIcon />}
                    onClick={() => {
                      setShowOnlyNeedsNotification(false);
                      setShowOnlyWithUnusedCodes(false);
                      setShowOnlyDelivered(false);
                      setShowOnlyNoSent(false);
                      setShowOnlyPhoneNoEmail(false);
                      setShowOnlyWithEmail(false);
                    }}
                    sx={{ ml: 'auto' }}
                  >
                    Filter zurücksetzen
                  </Button>
                )}
              </Box>
            </Collapse>
          </Stack>
        </Paper>

        {/* Bulk Actions Bar */}
        <Collapse in={selectedVolunteers.length > 0}>
          <Paper 
            sx={{ 
              p: 2, 
              mb: 3, 
              bgcolor: 'primary.light', 
              color: 'primary.contrastText',
              position: 'sticky',
              top: 0,
              zIndex: 100,
              boxShadow: 3,
            }}
          >
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              justifyContent="space-between" 
              alignItems={{ xs: 'stretch', sm: 'center' }} 
              spacing={2}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckIcon />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {selectedVolunteers.length} Freiwillige ausgewählt
                </Typography>
              </Box>
              
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<QrCodeIcon />}
                  onClick={() => setGenerateDialogOpen(true)}
                  disabled={generating}
                  sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
                >
                  QR-Codes erstellen
                </Button>
                <Button
                  variant="contained"
                  startIcon={<EmailIcon />}
                  onClick={() => setEmailDialogOpen(true)}
                  disabled={sendingEmails}
                  sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
                >
                  E-Mails senden
                </Button>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadWordQRCodes}
                  sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
                >
                  Word für Druck (QR-Codes)
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={() => setSelectedVolunteers([])}
                  sx={{ 
                    color: 'white', 
                    borderColor: 'white', 
                    '&:hover': { 
                      borderColor: 'white', 
                      bgcolor: 'rgba(255,255,255,0.1)' 
                    } 
                  }}
                >
                  Auswahl aufheben
                </Button>
                
              </Stack>
            </Stack>
          </Paper>
        </Collapse>

        {/* Error Alert */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }} 
            onClose={() => setError(null)}
            action={
              <Button size="small" onClick={() => setError(null)}>
                OK
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 8 }}>
            <Stack alignItems="center" spacing={2}>
              <CircularProgress size={48} />
              <Typography variant="body2" color="text.secondary">
                Freiwillige werden geladen...
              </Typography>
            </Stack>
          </Box>
        ) : (
          <>
            {/* Results Info */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {filteredVolunteers.length} von {volunteers.length} Freiwilligen
                {searchTerm && ` für "${searchTerm}"`}
                {selectedVolunteers.length > 0 && (
                  <Chip 
                    label={`${selectedVolunteers.length} ausgewählt`} 
                    color="primary" 
                    size="small" 
                    sx={{ ml: 1 }} 
                  />
                )}
              </Typography>
              {!isMobile && (
                <Typography variant="caption" color="text.secondary">
                  Nutzen Sie die Checkboxen zum Auswählen von Freiwilligen
                </Typography>
              )}
            </Box>

            {/* Content */}
            {filteredVolunteers.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Box sx={{ py: 4 }}>
                  <SearchIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Keine Freiwilligen gefunden
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {searchTerm 
                      ? `Keine Ergebnisse für "${searchTerm}". Versuchen Sie andere Suchbegriffe.`
                      : 'Keine Freiwilligen entsprechen den aktuellen Filterkriterien.'
                    }
                  </Typography>
                  <Stack direction="row" spacing={1} justifyContent="center">
                    {searchTerm && (
                      <Button onClick={() => setSearchTerm('')} startIcon={<ClearIcon />}>
                        Suche löschen
                      </Button>
                    )}
                    {[showOnlyNeedsNotification, showOnlyWithUnusedCodes, showOnlyDelivered].some(Boolean) && (
                      <Button 
                        onClick={() => {
                          setShowOnlyNeedsNotification(false);
                          setShowOnlyWithUnusedCodes(false);
                          setShowOnlyDelivered(false);
                        }}
                        startIcon={<FilterListIcon />}
                      >
                        Filter zurücksetzen
                      </Button>
                    )}
                  </Stack>
                </Box>
              </Paper>
            ) : (
              <Box>
                {/* Mobile View */}
                {filteredVolunteers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map(volunteer => renderVolunteerCard(volunteer))}
                
                {/* Pagination */}
                {filteredVolunteers.length > rowsPerPage && (
                  <Paper sx={{ mt: 2 }}>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25]}
                      component="div"
                      count={filteredVolunteers.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={(_, newPage) => setPage(newPage)}
                      onRowsPerPageChange={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10));
                        setPage(0);
                      }}
                      labelRowsPerPage="Einträge pro Seite:"
                      labelDisplayedRows={({ from, to, count }) => 
                        `${from}–${to} von ${count !== -1 ? count : `mehr als ${to}`}`
                      }
                    />
                  </Paper>
                )}
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Generate QR Codes Dialog */}
      <Dialog open={generateDialogOpen} onClose={handleCloseGenerateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>QR-Codes generieren</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            QR-Codes für {selectedVolunteers.length} ausgewählte Freiwillige generieren?
          </Typography>
          
          <FormControlLabel
            control={
              <Checkbox
                checked={includePartnerEntry}
                onChange={(e) => setIncludePartnerEntry(e.target.checked)}
              />
            }
            label="Kostenloser Eintritt für Partner einschließen"
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            label="Gültigkeitsdauer (Tage)"
            type="number"
            value={expiresInDays}
            onChange={(e) => setExpiresInDays(parseInt(e.target.value) || 0)}
            helperText="0 = Kein Ablaufdatum"
            InputProps={{ inputProps: { min: 0, max: 365 } }}
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            label="Kommentar (optional)"
            multiline
            rows={3}
            value={qrCodeComment}
            onChange={(e) => setQrCodeComment(e.target.value)}
            helperText="Zusätzlicher Kommentar für diesen QR-Code"
            placeholder="z.B. Sonderregelung, Anmerkungen..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGenerateDialog}>Abbrechen</Button>
          <Button
            onClick={handleGenerateQRCodes}
            variant="contained"
            disabled={generating}
            startIcon={generating ? <CircularProgress size={20} /> : <QrCodeIcon />}
          >
            {generating ? 'Generiere...' : 'Generieren'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>E-Mails mit QR-Codes senden</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            E-Mails mit QR-Codes an {selectedVolunteers.length} ausgewählte Freiwillige senden.
          </Typography>
          
          <TextField
            fullWidth
            label="Betreff"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            label="Nachricht"
            multiline
            rows={6}
            value={emailMessage}
            onChange={(e) => setEmailMessage(e.target.value)}
            helperText="Der QR-Code wird automatisch als Anhang hinzugefügt"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)}>Abbrechen</Button>
          <Button
            onClick={handleSendEmails}
            variant="contained"
            disabled={sendingEmails || !emailSubject.trim() || !emailMessage.trim()}
            startIcon={sendingEmails ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {sendingEmails ? 'Sende...' : 'Senden'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Volunteer Details Dialog */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={() => setDetailsDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          {selectedVolunteerDetails?.volunteerName} - QR-Code Details
        </DialogTitle>
        <DialogContent>
          {selectedVolunteerDetails && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Übersicht
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Box sx={{ flex: 1, minWidth: '200px' }}>
                  <Typography variant="body2" color="text.secondary">E-Mail:</Typography>
                  <Typography variant="body1">{selectedVolunteerDetails.volunteerEmail}</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: '200px' }}>
                  <Typography variant="body2" color="text.secondary">Telefon:</Typography>
                  <Typography variant="body1">{selectedVolunteerDetails.phoneNumber || 'Nicht verfügbar'}</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: '200px' }}>
                  <Typography variant="body2" color="text.secondary">Arbeitsstunden:</Typography>
                  <Typography variant="body1">{selectedVolunteerDetails.totalWorkingHours}h</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: '200px' }}>
                  <Typography variant="body2" color="text.secondary">Goodie verdient:</Typography>
                  <Typography variant="body1">{selectedVolunteerDetails.earnedGoodie ? 'Ja' : 'Nein'}</Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  QR-Codes ({selectedVolunteerDetails.qrCodes.length})
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<QrCodeIcon />}
                  onClick={() => {
                    setSelectedVolunteers([selectedVolunteerDetails.volunteerId]);
                    setDetailsDialogOpen(false);
                    setGenerateDialogOpen(true);
                  }}
                  disabled={selectedVolunteerDetails.totalWorkingHours === 0}
                >
                  Neuen QR-Code generieren
                </Button>
              </Box>
              
              {selectedVolunteerDetails.qrCodes.length === 0 ? (
                <Alert severity="info">Noch keine QR-Codes generiert.</Alert>
              ) : (
                <List>
                  {selectedVolunteerDetails.qrCodes.map(qrCode => (
                    <ListItem key={qrCode.id} divider>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2">
                              QR-Code vom {format(parseISO(qrCode.createdAt), 'dd.MM.yyyy HH:mm', { locale: de })}
                            </Typography>
                            {qrCode.includesPartnerEntry && (
                              <Chip label="+ Partner" size="small" color="primary" />
                            )}
                            {qrCode.isUsed && (
                              <Chip label="Verwendet" size="small" color="success" />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {qrCode.totalWorkingHours}h Arbeitsstunden
                            </Typography>
                            {qrCode.usedAt && (
                              <Typography variant="body2" color="text.secondary">
                                Verwendet am {format(parseISO(qrCode.usedAt), 'dd.MM.yyyy HH:mm', { locale: de })}
                                {qrCode.usedBy && ` von ${qrCode.usedBy}`}
                              </Typography>
                            )}
                            {qrCode.expiresAt && (
                              <Typography variant="body2" color="text.secondary">
                                Gültig bis {format(parseISO(qrCode.expiresAt), 'dd.MM.yyyy HH:mm', { locale: de })}
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                              {qrCode.isEmailSent && (
                                <Chip label="E-Mail gesendet" size="small" color="info" icon={<EmailIcon />} />
                              )}
                              {qrCode.isWhatsAppSent && (
                                <Chip label="WhatsApp gesendet" size="small" color="success" icon={<ChatIcon />} />
                              )}
                            </Box>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="QR-Code herunterladen">
                            <IconButton
                              size="small"
                              onClick={() => downloadQRCode(qrCode)}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                          {!qrCode.isUsed && (
                            <Tooltip title="QR-Code löschen">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteQRCode(qrCode.id)}
                                disabled={deletingQRCode === qrCode.id}
                              >
                                {deletingQRCode === qrCode.id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <DeleteIcon />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Preview Dialog */}
      <Dialog 
        open={qrCodePreviewOpen} 
        onClose={handleClosePreview} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          QR-Code Vorschau
          {selectedQRCodeForPreview && (
            <Typography variant="subtitle2" color="text.secondary">
              {selectedQRCodeForPreview.volunteerName} - {selectedQRCodeForPreview.totalWorkingHours}h
              {selectedQRCodeForPreview.includesPartnerEntry && ' (+ Partner)'}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {qrCodeImageUrl && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <img 
                src={qrCodeImageUrl} 
                alt="QR Code" 
                style={{ 
                  maxWidth: '100%', 
                  height: 'auto',
                  border: '1px solid #ddd',
                  borderRadius: '8px'
                }} 
              />
              <Typography variant="body2" color="text.secondary" align="center">
                Erstellt: {selectedQRCodeForPreview && format(parseISO(selectedQRCodeForPreview.createdAt), 'dd.MM.yyyy HH:mm', { locale: de })}
                <br />
                Status: {selectedQRCodeForPreview?.isUsed ? 'Verwendet' : 'Unbenutzt'}
                {selectedQRCodeForPreview?.isEmailSent && ' • E-Mail gesendet'}
                {selectedQRCodeForPreview?.isWhatsAppSent && ' • WhatsApp gesendet'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => selectedQRCodeForPreview && handleDownloadQRCode(selectedQRCodeForPreview)}
            startIcon={<DownloadIcon />}
          >
            Herunterladen
          </Button>
          <Button 
            onClick={() => selectedQRCodeForPreview && handleCopyQRCodeLink(selectedQRCodeForPreview)}
            startIcon={<LinkIcon />}
          >
            Link kopieren
          </Button>
          <Button onClick={handleClosePreview}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default QRCodeManagementPage;
